import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { Category } from '../../../types/index.ts';
import { CategoryIcon } from '../../../pages/Inventory.tsx';
import { resolveCategoryColor } from './useCategoryColors.ts';
import ReactDOM from 'react-dom';

const POPOVER_WIDTH = 320;
const POPOVER_MAX_HEIGHT = 420;
const GAP = 8;
const VIEWPORT_MARGIN = 16;
const POPOVER_Z = 1010; // .modal-overlay is z-index 1000 (global.css) -- same +10 rule as IconColorPopover

function allDescendantIds(catId: number, categories: Category[]): Set<number> {
  const ids = new Set<number>();
  const queue = [catId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const c of categories) {
      if (c.parent_id === id && !ids.has(c.id)) { ids.add(c.id); queue.push(c.id); }
    }
  }
  return ids;
}

// Ancestor chain from the top level DOWN TO AND INCLUDING `id` itself -- used to seed the
// drill-down stack so the popover opens already standing at the category's current level
// (siblings visible), with Back/breadcrumb able to walk all the way up to the top.
function ancestorChainIncluding(id: number | null, categories: Category[]): number[] {
  if (id === null) return [];
  const chain: number[] = [];
  let cur = categories.find((c) => c.id === id);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parent_id ? categories.find((c) => c.id === cur!.parent_id) : undefined;
  }
  return chain;
}

function pathLabel(id: number | null, categories: Category[]): string {
  if (id === null) return 'Top level';
  const names: string[] = [];
  let cur = categories.find((c) => c.id === id);
  while (cur) {
    names.unshift(cur.name);
    cur = cur.parent_id ? categories.find((c) => c.id === cur!.parent_id) : undefined;
  }
  return names.join(' › ');
}

// ─── Fix 2, rebuilt (2026-10-11): drill-down navigator restoring the ORIGINAL ParentCategoryPicker
// interaction (git history, Inventory.tsx ~2360-2526 pre-"Categories B" split) -- ancestor stack,
// breadcrumb, Back button, pinned "select this level" row, chevrons on rows with children -- but
// as its own portaled popover (not a position:absolute box) and restyled to match CategoryTree's
// colored-tile rows instead of the old plain list. Supersedes last round's flat-search MovePopover.
export function MoveCategoryPopover({
  anchorRef,
  categories,
  categoryColorMap,
  categoryId,
  categoryName,
  currentParentId,
  onMove,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  categories: Category[];
  categoryColorMap: Map<number, string>;
  categoryId: number;
  categoryName: string;
  currentParentId: number | null;
  onMove: (newParentId: number | null) => void;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; bottom: number; maxHeight: number } | null>(null);
  const [stack, setStack] = useState<number[]>(() => ancestorChainIncluding(currentParentId, categories));
  const [navDir, setNavDir] = useState<'in' | 'out'>('in');
  const [destSelected, setDestSelected] = useState(false);
  const [destId, setDestId] = useState<number | null>(null);

  const excluded = allDescendantIds(categoryId, categories);
  excluded.add(categoryId);
  const available = categories.filter((c) => !excluded.has(c.id));

  const currentLevelParentId = stack.length > 0 ? stack[stack.length - 1] : null;
  const currentLevelParentCat = currentLevelParentId !== null ? available.find((c) => c.id === currentLevelParentId) ?? null : null;
  const currentItems = available.filter((c) => (c.parent_id ?? null) === currentLevelParentId);

  function hasChildren(cat: Category): boolean {
    return available.some((c) => c.parent_id === cat.id);
  }
  function subCount(cat: Category): number {
    return available.filter((c) => c.parent_id === cat.id).length;
  }

  useLayoutEffect(() => {
    function recompute() {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
      const left = Math.min(Math.max(rect.left, VIEWPORT_MARGIN), window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN);
      setPos({ left, bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.min(POPOVER_MAX_HEIGHT, Math.max(spaceAbove, 160)) });
    }
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !containerRef.current) return;
      const focusable = containerRef.current.querySelectorAll<HTMLElement>('button, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insidePopover = containerRef.current?.contains(target);
      const insideAnchor = anchorRef.current?.contains(target);
      if (!insidePopover && !insideAnchor) onClose();
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleOutside);
    containerRef.current?.querySelector<HTMLElement>('button')?.focus();
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleOutside);
      previouslyFocused?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pos) return null;

  function drillInto(cat: Category) {
    setNavDir('in');
    setStack((prev) => [...prev, cat.id]);
  }
  function goBack() {
    setNavDir('out');
    setStack((prev) => prev.slice(0, -1));
  }
  function jumpToCrumb(index: number) {
    // index -1 = Top level itself
    setNavDir('out');
    setStack((prev) => prev.slice(0, index + 1));
  }
  function selectPinned() {
    if (currentLevelParentId === currentParentId) return; // disabled -- current location
    setDestId(currentLevelParentId);
    setDestSelected(true);
  }
  function selectLeaf(cat: Category) {
    setDestId(cat.id);
    setDestSelected(true);
  }

  const pinnedIsCurrentLocation = currentLevelParentId === currentParentId;
  const pinnedSelected = destSelected && destId === currentLevelParentId;
  const moveDisabled = !destSelected || destId === currentParentId;

  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-label={`Move ${categoryName}`}
      className="cat-popover-fade"
      style={{
        position: 'fixed', left: pos.left, bottom: pos.bottom, zIndex: POPOVER_Z,
        width: POPOVER_WIDTH, maxHeight: pos.maxHeight,
        background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
        borderRadius: 12, boxShadow: '0 20px 48px rgba(0,0,0,0.45)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
          Move &ldquo;{categoryName}&rdquo; to&hellip;
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {stack.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm combo1"
              onClick={goBack}
              style={{ fontSize: 11, padding: '2px 8px', flexShrink: 0 }}
            >
              &larr; Back
            </button>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span onClick={() => jumpToCrumb(-1)} style={{ cursor: 'pointer', color: stack.length === 0 ? 'var(--text-secondary)' : 'var(--text-muted)', fontWeight: stack.length === 0 ? 600 : 400 }}>
              Top level
            </span>
            {stack.map((id, i) => {
              const cat = available.find((c) => c.id === id);
              if (!cat) return null;
              const isLast = i === stack.length - 1;
              return (
                <span key={id}>
                  <span style={{ color: 'rgba(255,255,255,0.2)' }}> &rsaquo; </span>
                  <span
                    onClick={() => jumpToCrumb(i)}
                    style={{ cursor: 'pointer', color: isLast ? 'var(--text-secondary)' : 'var(--text-muted)', fontWeight: isLast ? 600 : 400 }}
                  >
                    {cat.name}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Level list -- re-keyed on stack so it remounts (and replays the fade/slide) on drill in/out */}
      <div
        key={stack.join('-')}
        className={navDir === 'in' ? 'cat-move-level-in' : 'cat-move-level-out'}
        style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
      >
        {/* Pinned "select this level" row */}
        <div
          onClick={selectPinned}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', cursor: pinnedIsCurrentLocation ? 'default' : 'pointer',
            borderBottom: '1px solid var(--border-subtle)', fontSize: 13,
            fontStyle: currentLevelParentCat ? 'italic' : 'normal',
            color: pinnedIsCurrentLocation ? 'var(--text-muted)' : pinnedSelected ? 'var(--accent-primary)' : 'var(--text-secondary)',
            background: pinnedSelected ? 'rgba(59,130,246,0.1)' : 'transparent',
            opacity: pinnedIsCurrentLocation ? 0.6 : 1,
          }}
          onMouseEnter={(e) => { if (!pinnedSelected && !pinnedIsCurrentLocation) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = pinnedSelected ? 'rgba(59,130,246,0.1)' : 'transparent'; }}
        >
          {currentLevelParentCat && (
            <div style={{
              width: 20, height: 20, borderRadius: 5, flexShrink: 0,
              background: `${resolveCategoryColor(currentLevelParentCat, categoryColorMap)}24`,
              color: resolveCategoryColor(currentLevelParentCat, categoryColorMap),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CategoryIcon iconName={currentLevelParentCat.icon_path} size={12} color="currentColor" />
            </div>
          )}
          <span style={{ flex: 1 }}>
            {currentLevelParentCat ? `Move into "${currentLevelParentCat.name}"` : 'Top level (no parent)'}
          </span>
          {pinnedIsCurrentLocation ? (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>Current location</span>
          ) : pinnedSelected && (
            <Check size={14} color="var(--accent-success)" style={{ flexShrink: 0 }} />
          )}
        </div>

        {currentItems.length === 0 ? (
          <div style={{ padding: '14px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
            {pinnedIsCurrentLocation
              ? `"${categoryName}" is already here, and there are no other categories at this level.`
              : 'No other categories at this level.'}
          </div>
        ) : (
          currentItems.map((cat) => {
            const color = resolveCategoryColor(cat, categoryColorMap);
            const children = hasChildren(cat);
            const isSelected = destSelected && destId === cat.id;
            return (
              <div
                key={cat.id}
                onClick={() => (children ? drillInto(cat) : selectLeaf(cat))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', cursor: 'pointer',
                  borderBottom: '1px solid var(--border-subtle)', fontSize: 13,
                  color: isSelected ? 'var(--accent-primary)' : 'var(--text-primary)',
                  background: isSelected ? 'rgba(59,130,246,0.1)' : 'transparent',
                }}
                onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = isSelected ? 'rgba(59,130,246,0.1)' : 'transparent'; }}
              >
                <div style={{
                  width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                  background: `${color}24`, color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CategoryIcon iconName={cat.icon_path} size={13} color="currentColor" />
                </div>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</span>
                {children && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: '#60a5fa', background: 'rgba(59,130,246,0.15)',
                    borderRadius: 999, padding: '2px 7px', flexShrink: 0, whiteSpace: 'nowrap',
                  }}>
                    {subCount(cat)} sub
                  </span>
                )}
                {isSelected && !children && <Check size={14} color="var(--accent-success)" style={{ flexShrink: 0 }} />}
                {children && <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Moving &ldquo;{categoryName}&rdquo; &rarr; {destSelected ? pathLabel(destId, available) : '—'}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary combo2"
            disabled={moveDisabled}
            onClick={() => { onMove(destId); onClose(); }}
          >
            Move
          </button>
        </div>
      </div>

      <style>{`
        .cat-popover-fade { animation: catPopoverFadeIn 0.15s ease; }
        @keyframes catPopoverFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .cat-move-level-in { animation: catMoveLevelIn 0.15s ease; }
        .cat-move-level-out { animation: catMoveLevelOut 0.15s ease; }
        @keyframes catMoveLevelIn { from { opacity: 0; transform: translateX(10px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes catMoveLevelOut { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
        @media (prefers-reduced-motion: reduce) {
          .cat-popover-fade, .cat-move-level-in, .cat-move-level-out { animation: none; }
        }
      `}</style>
    </div>,
    document.body
  );
}
